/**
 * View-model helpers for the student course-registration calendar selector.
 *
 * The selector is fed by `studentRegisteredCalendars`, which only returns
 * calendars the student already has a course registration for. The current
 * calendar therefore has to be added explicitly — a student who has not
 * registered yet would otherwise have no way to pick the semester they are
 * actually registering for — and de-duplicated once they have registered, or it
 * would appear twice.
 */

export interface CalendarViewOption {
  label: string;
  value: string;
}

/** Shown when the current calendar is known but its session/term names are not. */
export const CURRENT_CALENDAR_FALLBACK_LABEL = 'Current Semester';

export const CURRENT_CALENDAR_SUFFIX = ' (Current)';

/** `2026/2027 - 1st Semester`, or an empty string when either half is missing. */
export function formatCalendarLabel(
  sessionName?: string | null,
  termName?: string | null,
): string {
  if (!sessionName || !termName) return '';

  return `${sessionName} - ${termName}`;
}

/**
 * Current calendar first and tagged, then the student's other registered
 * calendars in the order the API returned them (newest first).
 */
export function buildCalendarViewOptions(
  current: CalendarViewOption | null,
  registered: CalendarViewOption[],
): CalendarViewOption[] {
  if (!current?.value) return [...registered];

  return [
    {
      label: `${current.label || CURRENT_CALENDAR_FALLBACK_LABEL}${CURRENT_CALENDAR_SUFFIX}`,
      value: current.value,
    },
    ...registered.filter(option => option.value !== current.value),
  ];
}
